
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Tv, Plus, Play, Sparkles, Users, RefreshCw, Dna, X, ChevronRight, Search, Check } from 'lucide-react';
import { toast } from 'sonner';
import VizioPairingPrompt from '../components/VizioPairingPrompt';
import VizioPairingModal from '../components/VizioPairingModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL = 60000;
const VIZIO_POLL_INTERVAL = 30000;

const SERVICE_META = {
  netflix:        { name: 'Netflix',      color: '#E50914' },
  hulu:           { name: 'Hulu',         color: '#1CE783' },
  disney_plus:    { name: 'Disney+',      color: '#113CCF' },
  hbo_max:        { name: 'HBO Max',      color: '#B535F6' },
  amazon_prime:   { name: 'Prime Video',  color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',    color: '#555555' },
  peacock:        { name: 'Peacock',      color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',   color: '#0064FF' },
};

const MOODS = [
  { id: 'happy',       label: 'Happy',     emoji: '😄' },
  { id: 'stressed',    label: 'Chill',     emoji: '😤' },
  { id: 'bored',       label: 'Bored',     emoji: '😑' },
  { id: 'adventurous', label: 'Adventure', emoji: '🔥' },
  { id: 'romantic',    label: 'Romantic',  emoji: '❤️' },
  { id: 'sad',         label: 'Sad',       emoji: '😢' },
];

function getDeepLink(serviceId, title) {
  const q = encodeURIComponent(title);
  const urls = {
    netflix:        `https://www.netflix.com/search?q=${q}`,
    hulu:           `https://www.hulu.com/search?q=${q}`,
    disney_plus:    `https://www.disneyplus.com/search?q=${q}`,
    hbo_max:        `https://play.max.com/search?q=${q}`,
    amazon_prime:   `https://www.amazon.com/s?k=${q}&i=instant-video`,
    apple_tv:       `https://tv.apple.com/search?term=${q}`,
    peacock:        `https://www.peacocktv.com/search?q=${q}`,
    paramount_plus: `https://www.paramountplus.com/search?q=${q}`,
  };
  return urls[serviceId] || '';
}

function DNARing({ percent }) {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;
  return (
    <div style={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}>
      <svg width="72" height="72" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="36" cy="36" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
        <circle cx="36" cy="36" r={r} fill="none" stroke="url(#dnaRingGrad)" strokeWidth="6"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
        <defs>
          <linearGradient id="dnaRingGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7c3aed" />
            <stop offset="100%" stopColor="#4A9EFF" />
          </linearGradient>
        </defs>
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 900, color: '#fff', lineHeight: 1 }}>{percent}%</span>
      </div>
    </div>
  );
}

function TraktPrompt({ onConnect, onDismiss, connecting }) {
  return (
    <div style={{ position: 'relative', padding: 16, borderRadius: 16, border: '1px solid rgba(232,38,45,0.3)', background: 'linear-gradient(135deg, #1a0505, #0a0a0a)', marginBottom: 16 }}>
      <button onClick={onDismiss} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#555', padding: 4 }}>
        <X size={14} />
      </button>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(232,38,45,0.15)', border: '1px solid rgba(232,38,45,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>
          📺
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>Import your watch history</p>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: '0 0 10px', lineHeight: 1.5 }}>
            Connect Trakt to automatically import everything you've already watched.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={onConnect} disabled={connecting}
              style={{ background: '#E8262D', border: 'none', borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', opacity: connecting ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
              {connecting ? <><div style={{ width: 10, height: 10, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />Connecting...</> : 'Connect Trakt'}
            </button>
            <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: 12, padding: '6px 4px' }}>
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContinueWatchingHero({ show, onLaunch }) {
  const svc = SERVICE_META[show.service_id] || {};
  const hasEpisode = show.current_season && show.current_episode;
  const progressPct = hasEpisode ? Math.min(100, (show.current_episode / 10) * 100) : 0;

  return (
    <div
      onClick={() => onLaunch(show)}
      style={{
        position: 'relative',
        borderRadius: 20,
        overflow: 'hidden',
        background: '#111',
        border: '1px solid rgba(255,255,255,0.08)',
        cursor: 'pointer',
        height: 280,
      }}
    >
      {show.image_url ? (
        <>
          <img
            src={show.image_url.replace('/w300/', '/w780/')}
            alt={show.title}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.1) 100%)' }} />
        </>
      ) : (
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #1a0a2e, #0a1a2e)' }} />
      )}

      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0 16px 16px' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center',
          background: `${svc.color || '#fff'}22`,
          border: `1px solid ${svc.color || '#fff'}44`,
          borderRadius: 20, padding: '3px 10px',
          fontSize: 10, fontWeight: 700, color: svc.color || '#fff',
          marginBottom: 8,
        }}>
          {svc.name || show.service_id}
        </div>

        <h2 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: '0 0 4px', lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
          {show.title}
        </h2>

        {show.tonight_reason && (
          <p style={{ fontSize: 11, color: '#a78bfa', margin: '0 0 4px', fontWeight: 600 }}>
            ✦ {show.tonight_reason}
          </p>
        )}

        {hasEpisode && (
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: '0 0 10px' }}>
            Season {show.current_season} · Episode {show.current_episode}
          </p>
        )}

        {hasEpisode && (
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 3, height: 3, marginBottom: 12 }}>
            <div style={{ height: 3, borderRadius: 3, background: svc.color || '#7c3aed', width: `${progressPct}%` }} />
          </div>
        )}

        <button
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: svc.color || '#7c3aed', color: '#fff',
            border: 'none', borderRadius: 24, padding: '10px 22px',
            fontSize: 14, fontWeight: 800, cursor: 'pointer',
          }}
        >
          <Play size={14} style={{ fill: '#fff' }} />
          Resume
        </button>
      </div>
    </div>
  );
}

function MiniShowCard({ show, onLaunch }) {
  const svc = SERVICE_META[show.service_id] || {};
  return (
    <div
      onClick={() => onLaunch(show)}
      style={{
        flexShrink: 0, width: 110,
        borderRadius: 14, overflow: 'hidden',
        background: '#111',
        border: '1px solid rgba(255,255,255,0.07)',
        cursor: 'pointer', position: 'relative',
      }}
    >
      {show.image_url ? (
        <img src={show.image_url} alt={show.title}
          style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }} />
      ) : (
        <div style={{ width: '100%', height: 80, background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Tv size={18} style={{ color: '#444' }} />
        </div>
      )}
      <div style={{ position: 'absolute', top: 5, right: 5, width: 7, height: 7, borderRadius: '50%', background: svc.color || '#fff' }} />
      <div style={{ padding: '6px 8px 8px' }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: '#fff', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {show.title}
        </p>
        {show.current_season && show.current_episode && (
          <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', margin: '0 0 4px' }}>
            S{show.current_season}E{show.current_episode}
          </p>
        )}
        {show.current_episode && (
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 2, height: 2 }}>
            <div style={{ height: 2, borderRadius: 2, background: svc.color || '#7c3aed', width: `${Math.min(100, (show.current_episode / 10) * 100)}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}


export default function Dashboard() {
  const { user, getToken } = useAuth();
  const [shows, setShows] = useState([]);
  const [hubData, setHubData] = useState(null);
  const [qmates, setQmates] = useState([]);
  const [dna, setDna] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [showTraktPrompt, setShowTraktPrompt] = useState(false);
  const [traktConnecting, setTraktConnecting] = useState(false);

  const [qudeUpIndex, setQudeUpIndex] = useState(0);
  const [showMoods, setShowMoods] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodResults, setMoodResults] = useState([]);
  const [moodResultIndex, setMoodResultIndex] = useState(0);
  const [moodLoading, setMoodLoading] = useState(false);

  const [unpairedVizios, setUnpairedVizios] = useState([]);
  const [vizioModalTarget, setVizioModalTarget] = useState(null);

  const pollRef = useRef(null);
  const vizioPollRef = useRef(null);

  // ── Derived data ────────────────────────────────────────────────────────
  const nowPlaying = hubData?.now_playing || null;

  // Continue Watching: prefer hub data (live viewing_state) over shows collection.
  // Hub cards use `service` field; adapt to `service_id` that the card components expect.
  const hubToShow = (card) => ({
    ...card,
    show_id: card.show_key,
    service_id: card.service,
  });

  const hubWatching = hubData?.up_next?.map(hubToShow) || [];
  const watchingShows = hubWatching.length > 0
    ? hubWatching
    : shows.filter(s => s.status === 'watching' && s.current_season);
  const heroShow = watchingShows[0] || null;
  const restShows = watchingShows.slice(1);

  // Qude Up pool: everything NOT in the Continue Watching list
  const watchingIds = new Set(watchingShows.map(s => s.show_id));
  const qudeUpPool = shows.filter(s => !watchingIds.has(s.show_id));
  const qudeUpShow = qudeUpPool.length > 0
    ? qudeUpPool[qudeUpIndex % qudeUpPool.length]
    : null;

  const topServiceMeta = dna?.top_service ? SERVICE_META[dna.top_service] : null;
  const currentMoodShow = moodResults[moodResultIndex] || null;
  const firstName = user?.firstName || '';

  // ── Launch ───────────────────────────────────────────────────────────────
  const launchShow = async (show) => {
    if (!show) return;
    const popup = window.open('about:blank', '_blank');
    // Hub cards use show_key (e.g. "tmdb:tv:1396") not a DB show_id — use the v2 endpoint.
    const isHubCard = !show.show_id || show.show_id.startsWith('tmdb:') || show.show_id.startsWith('title:');
    if (isHubCard) {
      try {
        const token = await getToken();
        const params = new URLSearchParams({
          service_id: show.service_id || '',
          media_type: show.media_type || 'tv',
          title: show.title || '',
          ...(show.tmdb_id ? { tmdb_id: show.tmdb_id } : {}),
        });
        const res = await axios.get(`${API}/launch-url-v2?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        });
        const url = res.data?.url;
        if (url && popup) { popup.location.href = url; return; }
      } catch {}
      const fallback = getDeepLink(show.service_id, show.title);
      if (fallback && popup) { popup.location.href = fallback; }
      else if (popup) { popup.close(); }
      return;
    }
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/launch-url/${show.show_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      });
      const url = res.data?.url;
      if (url && popup) { popup.location.href = url; }
      else if (url) { window.open(url, '_blank'); }
      else if (popup) { popup.close(); }
    } catch {
      let fallback = show.deep_link;
      if (!fallback && show.service_id && show.title) fallback = getDeepLink(show.service_id, show.title);
      if (!fallback && show.title) fallback = `https://www.google.com/search?q=${encodeURIComponent(show.title)}`;
      if (fallback && popup) { popup.location.href = fallback; }
      else if (popup) { popup.close(); }
    }
  };

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (silent = false) => {
    if (document.visibilityState === 'hidden') return;
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [showsRes, hubRes, qmatesRes, dnaRes, traktRes] = await Promise.all([
        axios.get(`${API}/shows`, { headers }),
        axios.get(`${API}/hub`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/qmates`, { headers }),
        axios.get(`${API}/watch-dna`, { headers }),
        axios.get(`${API}/trakt/status`, { headers }),
      ]);
      setShows(showsRes.data);
      setHubData(hubRes.data);
      setQmates(qmatesRes.data.filter(q => q.status === 'accepted'));
      setDna(dnaRes.data);
      if (!traktRes.data.connected && showsRes.data.length === 0) {
        if (!sessionStorage.getItem('trakt_prompt_dismissed')) setShowTraktPrompt(true);
      } else {
        setShowTraktPrompt(false);
      }
      if (silent) setLastSynced(new Date());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  }, [getToken]);

  const refreshVizioStatus = useCallback(() => {
    if (!window.QudeAndroid?.getKnownVizios) return;
    try {
      const list = JSON.parse(window.QudeAndroid.getKnownVizios() || '[]');
      const dismissed = JSON.parse(localStorage.getItem('vizio_pairing_dismissed_ips') || '[]');
      setUnpairedVizios(list.filter(tv => tv?.ip && !tv.paired && !dismissed.includes(tv.ip)));
    } catch (e) { console.error('refreshVizioStatus error:', e); }
  }, []);

  useEffect(() => { if (user) fetchData(false); }, [user, fetchData]);

  useEffect(() => {
    if (!user) return;
    pollRef.current = setInterval(() => fetchData(true), POLL_INTERVAL);
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchData(true); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(pollRef.current); document.removeEventListener('visibilitychange', onVisibility); };
  }, [user, fetchData]);

  useEffect(() => {
    if (!user) return;
    refreshVizioStatus();
    vizioPollRef.current = setInterval(refreshVizioStatus, VIZIO_POLL_INTERVAL);
    const onVisibility = () => { if (document.visibilityState === 'visible') refreshVizioStatus(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(vizioPollRef.current); document.removeEventListener('visibilitychange', onVisibility); };
  }, [user, refreshVizioStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('trakt') === 'connected') {
      setShowTraktPrompt(false);
      axios.post(`${API}/trakt/sync`, {}, { headers: { Authorization: '' } }).catch(() => {});
      fetchData(false);
      window.history.replaceState({}, '', '/dashboard');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTraktConnect = async () => {
    setTraktConnecting(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/trakt/connect`, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch { setTraktConnecting(false); }
  };

  const handleMoodSelect = async (mood) => {
    setSelectedMood(mood.id);
    setMoodResults([]);
    setMoodResultIndex(0);
    setMoodLoading(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${API}/mood/queue`, { mood: mood.id }, { headers: { Authorization: `Bearer ${token}` } });
      setMoodResults((res.data.results || []).filter((r) => !r.in_progress));
    } catch (err) { console.error('MoodQ failed:', err); }
    finally { setMoodLoading(false); }
  };

  const vizioToPrompt = unpairedVizios[0] || null;

  return (
    <div className="max-w-2xl mx-auto" data-testid="dashboard-page">

      {showTraktPrompt && (
        <TraktPrompt
          onConnect={handleTraktConnect}
          onDismiss={() => { sessionStorage.setItem('trakt_prompt_dismissed', '1'); setShowTraktPrompt(false); }}
          connecting={traktConnecting}
        />
      )}

      {vizioToPrompt && (
        <div className="mb-4">
          <VizioPairingPrompt
            tvName={vizioToPrompt.name}
            ipAddress={vizioToPrompt.ip}
            onPair={(ip, name) => setVizioModalTarget({ ipAddress: ip, tvName: name })}
            onDismiss={() => {
              try {
                const dismissed = JSON.parse(localStorage.getItem('vizio_pairing_dismissed_ips') || '[]');
                if (!dismissed.includes(vizioToPrompt.ip)) dismissed.push(vizioToPrompt.ip);
                localStorage.setItem('vizio_pairing_dismissed_ips', JSON.stringify(dismissed));
              } catch (e) {}
              setUnpairedVizios(prev => prev.filter(tv => tv.ip !== vizioToPrompt.ip));
            }}
          />
        </div>
      )}

      {/* Now on TV */}
      {nowPlaying && (
        <div className="mb-4" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 16, border: '1px solid rgba(74,222,128,0.2)', background: 'rgba(74,222,128,0.05)' }}>
          <span style={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#4ade80', opacity: 0.4, animation: 'ping 1s cubic-bezier(0,0,0.2,1) infinite' }} />
            <span style={{ position: 'relative', display: 'block', width: 10, height: 10, borderRadius: '50%', background: '#22c55e' }} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nowPlaying.title}</p>
            <p style={{ fontSize: 11, color: 'rgba(74,222,128,0.7)', margin: 0 }}>
              Now on {nowPlaying.device_name || nowPlaying.device_type}
              {nowPlaying.season && nowPlaying.episode ? ` · S${nowPlaying.season}E${nowPlaying.episode}` : ''}
            </p>
          </div>
        </div>
      )}

      {/* Welcome */}
      <div className="mb-5 pt-1">
        <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
          Welcome back{firstName ? `, ${firstName}` : ''} 👋
        </h1>
        {lastSynced && (
          <p className="text-[11px] text-neutral-700 mt-0.5">
            Synced {lastSynced.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>

      {/* ── Continue Watching ── */}
      <div className="mb-6">
        <div className="flex items-center gap-1.5 mb-3">
          <Play size={13} className="text-violet-400" />
          <span className="text-[11px] font-black tracking-widest text-neutral-500 uppercase">
            Continue Watching
          </span>
        </div>

        {heroShow ? (
          <>
            <ContinueWatchingHero show={heroShow} onLaunch={launchShow} />
            {restShows.length > 0 && (
              <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, marginTop: 10, scrollbarWidth: 'none' }}>
                {restShows.map(show => (
                  <MiniShowCard key={show.show_id} show={show} onLaunch={launchShow} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="rounded-2xl border border-white/5 bg-[#0A0A0A] p-8 text-center">
            <Tv size={36} className="text-neutral-700 mx-auto mb-3" />
            <p className="text-white font-bold mb-1">Nothing in progress</p>
            <p className="text-neutral-500 text-sm mb-4">Add shows to your watchlist and start watching</p>
            <Link to="/watchlist">
              <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}>
                <Plus size={14} /> Add a Show
              </button>
            </Link>
          </div>
        )}
      </div>

      {/* ── Qude Up ── */}
      <div className="mb-6">
        <div className="flex items-center gap-1.5 mb-3">
          <Sparkles size={13} className="text-[#4A9EFF]" />
          <span className="text-[11px] font-black tracking-widest text-neutral-500 uppercase">Qude Up</span>
          <span className="text-[11px] text-neutral-700 ml-1">— something different</span>
        </div>

        {qudeUpShow ? (
          <div style={{ borderRadius: 20, border: '1px solid rgba(255,255,255,0.08)', background: '#0f0f0f', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 12, padding: '14px 14px 12px' }}>
              {qudeUpShow.image_url ? (
                <img src={qudeUpShow.image_url} alt={qudeUpShow.title}
                  style={{ width: 64, height: 90, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{ width: 64, height: 90, borderRadius: 12, background: '#1a1a1a', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Tv size={20} style={{ color: '#444' }} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 10, fontWeight: 700, color: SERVICE_META[qudeUpShow.service_id]?.color || '#fff', margin: '0 0 4px' }}>
                  {SERVICE_META[qudeUpShow.service_id]?.name || qudeUpShow.service_id}
                </p>
                <p style={{ fontSize: 16, fontWeight: 800, color: '#fff', margin: '0 0 4px', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {qudeUpShow.title}
                </p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: '0 0 12px' }}>
                  {qudeUpShow.status === 'watching' && qudeUpShow.current_season
                    ? `Resume S${qudeUpShow.current_season}E${qudeUpShow.current_episode}`
                    : 'Not started yet'}
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => launchShow(qudeUpShow)}
                    style={{ background: SERVICE_META[qudeUpShow.service_id]?.color || '#7c3aed', border: 'none', borderRadius: 20, padding: '7px 16px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Play size={11} style={{ fill: '#fff' }} /> Play
                  </button>
                  <button onClick={() => setQudeUpIndex(i => i + 1)}
                    style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '7px 14px', fontSize: 12, color: 'rgba(255,255,255,0.4)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <RefreshCw size={11} /> Next
                  </button>
                </div>
              </div>
            </div>

            {/* Mood toggle */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: '10px 14px' }}>
              <button onClick={() => setShowMoods(m => !m)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.35)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, padding: 0 }}>
                🎭 Pick by mood instead
                <ChevronRight size={12} style={{ transform: showMoods ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>

              {showMoods && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                    {MOODS.map(mood => (
                      <button key={mood.id} onClick={() => handleMoodSelect(mood)}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '10px 4px', borderRadius: 12,
                          border: selectedMood === mood.id ? '1px solid rgba(124,58,237,0.6)' : '1px solid rgba(255,255,255,0.06)',
                          background: selectedMood === mood.id ? 'rgba(124,58,237,0.2)' : 'rgba(255,255,255,0.03)', cursor: 'pointer' }}>
                        <span style={{ fontSize: 20 }}>{mood.emoji}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, color: selectedMood === mood.id ? '#fff' : 'rgba(255,255,255,0.4)' }}>
                          {mood.label}
                        </span>
                      </button>
                    ))}
                  </div>

                  {moodLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
                      <div style={{ width: 12, height: 12, border: '2px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Finding something for your mood...
                    </div>
                  )}

                  {!moodLoading && currentMoodShow && (
                    <div style={{ borderRadius: 14, border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.3)', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', gap: 10, padding: '10px' }}>
                        {currentMoodShow.poster_url ? (
                          <img src={currentMoodShow.poster_url} alt={currentMoodShow.title}
                            style={{ width: 44, height: 62, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 44, height: 62, borderRadius: 8, background: '#1a1a1a', flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {currentMoodShow.title}
                          </p>
                          {currentMoodShow.rating > 0 && (
                            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: '0 0 8px' }}>⭐ {currentMoodShow.rating}</p>
                          )}
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(currentMoodShow.title)}`, '_blank')}
                              style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)', border: 'none', borderRadius: 12, padding: '5px 12px', fontSize: 11, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
                              Play
                            </button>
                            <button onClick={() => setMoodResultIndex(i => (i + 1) % moodResults.length)}
                              style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '5px 10px', fontSize: 11, color: 'rgba(255,255,255,0.4)', cursor: 'pointer' }}>
                              Next
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)', background: '#0a0a0a', padding: '20px', textAlign: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, margin: 0 }}>Add more shows to get suggestions here</p>
          </div>
        )}
      </div>

      {/* ── Watch DNA ── */}
      {dna && (
        <Link to="/watch-dna" style={{ textDecoration: 'none', display: 'block' }} className="block mb-4">
          <div style={{ padding: 16, borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)', background: 'linear-gradient(135deg, #1a0a2e, #0a1a2e)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
              <Dna size={13} style={{ color: '#a78bfa' }} />
              <span style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Watch DNA</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#a78bfa', fontWeight: 600 }}>View →</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <DNARing percent={dna.completion_rate} />
              <div>
                <p style={{ fontSize: 10, color: '#a78bfa', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 2px' }}>Your type</p>
                <p style={{ fontSize: 20, fontWeight: 900, color: '#fff', margin: 0 }}>The {dna.dna_label}</p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 14 }}>
              {[
                { value: dna.total_shows, label: 'Shows' },
                { value: dna.watched, label: 'Finished' },
                { value: topServiceMeta?.name || '—', label: 'Top Service', color: topServiceMeta?.color },
              ].map((item, i) => (
                <div key={i} style={{ borderRadius: 12, background: 'rgba(0,0,0,0.3)', padding: '8px 12px', textAlign: 'center' }}>
                  <p style={{ fontSize: 15, fontWeight: 900, color: item.color || '#fff', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.value}
                  </p>
                  <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', margin: 0 }}>{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </Link>
      )}

      {/* ── QMates ── */}
      <div style={{ padding: 16, borderRadius: 20, border: '1px solid rgba(255,255,255,0.06)', background: '#0a0a0a' }} className="mb-4">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
          <Users size={13} style={{ color: '#67e8f9' }} />
          <span style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Watch Together</span>
        </div>
        {qmates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4px 0 8px' }}>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, margin: '0 0 10px' }}>No QMates yet</p>
            <Link to="/qmate">
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a78bfa', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Plus size={12} /> Invite a QMate
              </button>
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {qmates.slice(0, 3).map(qmate => (
              <div key={qmate.qmate_id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(124,58,237,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                  {qmate.picture ? (
                    <img src={qmate.picture} alt={qmate.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#a78bfa' }}>
                      {(qmate.name || qmate.username || '?')[0].toUpperCase()}
                    </span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {qmate.name || qmate.username}
                  </p>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: 0 }}>Connected</p>
                </div>
                <Link to="/qmate">
                  <span style={{ fontSize: 11, color: '#67e8f9', fontWeight: 600 }}>View →</span>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {vizioModalTarget && (
        <VizioPairingModal
          ipAddress={vizioModalTarget.ipAddress}
          deviceName={vizioModalTarget.tvName}
          onDismiss={() => { setVizioModalTarget(null); refreshVizioStatus(); }}
          onPaired={(ip) => { setUnpairedVizios(prev => prev.filter(tv => tv.ip !== ip)); setVizioModalTarget(null); }}
        />
      )}

      {/* Manual log modal */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
